import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { KeyRound, ShieldCheck, Eye, EyeOff, Loader2, LogOut, CheckCircle2, XCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Mandatory password-setup gate for invited users (mustChangePassword=true).
 * Mobile-friendly, validates strength inline, blocks the dashboard until
 * the temporary password has been rotated.
 */
function evaluateStrength(pw) {
  const checks = {
    length: pw.length >= 8,
    letter: /[a-zA-Z]/.test(pw),
    digit: /[0-9]/.test(pw),
    symbol: /[^a-zA-Z0-9]/.test(pw),
  };
  const score = Object.values(checks).filter(Boolean).length;
  let label = "Weak";
  let color = "bg-rose-500";
  if (score >= 4) {
    label = "Strong";
    color = "bg-emerald-500";
  } else if (score === 3) {
    label = "Good";
    color = "bg-amber-500";
  } else if (score === 2) {
    label = "Fair";
    color = "bg-amber-400";
  }
  return { checks, score, label, color, percent: (score / 4) * 100 };
}

export default function ChangePasswordPage({ onCompleted }) {
  const { user, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const strength = useMemo(() => evaluateStrength(newPassword), [newPassword]);
  const meetsMin = strength.checks.length && strength.checks.letter && strength.checks.digit;
  const matches = newPassword.length > 0 && newPassword === confirm;
  const canSubmit = currentPassword && meetsMin && matches && !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!meetsMin) {
      setError("Please satisfy all the password requirements below.");
      return;
    }
    if (!matches) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        currentPassword,
        newPassword,
      });
      toast.success("Password updated — welcome aboard!");
      if (typeof onCompleted === "function") {
        await onCompleted();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Could not update password.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="change-password-page"
      className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-amber-50 to-slate-50 p-4"
    >
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full max-w-md"
      >
        <Card className="rounded-3xl shadow-lg">
          <CardContent className="p-7 sm:p-8">
            <div className="mb-5 flex items-start gap-3">
              <div className="rounded-2xl bg-slate-900 p-3 text-amber-200 shadow-sm">
                <KeyRound size={22} />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight sm:text-2xl">Set your password</h1>
                <p className="mt-0.5 text-sm text-slate-600">
                  You're signed in as <strong>{user?.email}</strong>. For security, please replace the temporary
                  password before you continue.
                </p>
              </div>
            </div>

            {error && (
              <div
                data-testid="change-password-error"
                className="mb-3 rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-800"
              >
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <label className="block text-sm font-medium text-slate-700">
                Temporary password
                <div className="relative mt-1">
                  <input
                    data-testid="change-password-current"
                    type={show ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    inputMode="text"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full rounded-xl border bg-white p-3 pr-10 text-base font-normal"
                  />
                </div>
              </label>

              <label className="block text-sm font-medium text-slate-700">
                New password
                <div className="relative mt-1">
                  <input
                    data-testid="change-password-new"
                    type={show ? "text" : "password"}
                    required
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full rounded-xl border bg-white p-3 pr-10 text-base font-normal"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    aria-label={show ? "Hide password" : "Show password"}
                    data-testid="toggle-password-visibility"
                  >
                    {show ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </label>

              <label className="block text-sm font-medium text-slate-700">
                Confirm new password
                <input
                  data-testid="change-password-confirm"
                  type={show ? "text" : "password"}
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1 w-full rounded-xl border bg-white p-3 text-base font-normal"
                />
                {confirm.length > 0 && (
                  <p
                    data-testid="change-password-match"
                    className={`mt-1 text-xs ${matches ? "text-emerald-700" : "text-rose-700"}`}
                  >
                    {matches ? "Passwords match" : "Passwords don't match yet"}
                  </p>
                )}
              </label>

              {/* Strength meter */}
              {newPassword.length > 0 && (
                <div data-testid="change-password-strength" className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">Password strength</span>
                    <span
                      data-testid="change-password-strength-label"
                      className={`font-semibold ${
                        strength.label === "Strong"
                          ? "text-emerald-700"
                          : strength.label === "Good"
                            ? "text-amber-700"
                            : "text-rose-700"
                      }`}
                    >
                      {strength.label}
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      className={`h-full ${strength.color} transition-all`}
                      style={{ width: `${strength.percent}%` }}
                    />
                  </div>
                  <ul className="grid grid-cols-2 gap-1 text-xs text-slate-600">
                    <RequirementRow ok={strength.checks.length} label="At least 8 characters" />
                    <RequirementRow ok={strength.checks.letter} label="Contains a letter" />
                    <RequirementRow ok={strength.checks.digit} label="Contains a number" />
                    <RequirementRow ok={strength.checks.symbol} label="Symbol (optional)" optional />
                  </ul>
                </div>
              )}

              <Button
                data-testid="change-password-submit"
                type="submit"
                disabled={!canSubmit}
                className="w-full rounded-2xl bg-slate-900 py-6 text-base hover:bg-slate-800 disabled:bg-slate-300"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" /> Updating…
                  </>
                ) : (
                  <>
                    <ShieldCheck size={16} className="mr-2" /> Set password & continue
                  </>
                )}
              </Button>
            </form>

            <button
              type="button"
              onClick={() => logout()}
              data-testid="change-password-signout"
              className="mt-4 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800"
            >
              <LogOut size={12} /> Sign out instead
            </button>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

function RequirementRow({ ok, label, optional }) {
  return (
    <li className="flex items-center gap-1.5">
      {ok ? (
        <CheckCircle2 size={12} className="text-emerald-600" />
      ) : (
        <XCircle size={12} className={optional ? "text-slate-300" : "text-rose-400"} />
      )}
      <span className={ok ? "text-slate-800" : "text-slate-500"}>{label}</span>
    </li>
  );
}
